import {
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { dynamicInvoicePageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { ArrowSmallRight, VyIcon } from '@vy/ui-design-system';
import type { IntegrationProviders } from '@vy/types';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import { useMenu } from '../../../../../hooks/use-menu';

type SyncInvoiceMenuProps = {
  handleMenuClose: () => void;
  connectedIntegrations: IntegrationProviders[];
  onSyncToStripe?: () => void;
  onSyncToNetSuite?: () => void;
  onSyncToNetSuiteSalesOrder?: () => void;
  onSyncToQuickBooks?: () => void;
  onSyncToMorning?: () => void;
  onSyncToMarketplace?: () => void;
  onSyncToXero?: () => void;
  onSyncToPaddle?: () => void;
  syncToNetSuiteDisabled?: boolean;
  syncToStripeDisabled?: boolean;
  isInvoiceSyncable?: boolean;
  isMarketplaceSynced?: boolean;
  disabled?: boolean;
};

export const SyncInvoiceMenu: React.FC<SyncInvoiceMenuProps> = ({
  disabled,
  onSyncToStripe,
  onSyncToNetSuite,
  onSyncToNetSuiteSalesOrder,
  onSyncToQuickBooks,
  onSyncToMorning,
  onSyncToMarketplace,
  onSyncToXero,
  onSyncToPaddle,
  syncToNetSuiteDisabled,
  connectedIntegrations,
  handleMenuClose,
  syncToStripeDisabled,
  isInvoiceSyncable = false,
  isMarketplaceSynced = false,
}) => {
  const {
    isOpen: isSyncMenuOpen,
    handleMenuOpen: handleSyncMenuOpen,
    handleMenuClose: handleSyncMenuClose,
    anchorElement: syncMenuAnchorElement,
  } = useMenu();

  const isQuickBooksConnected = connectedIntegrations.includes(INTEGRATION_PROVIDERS.quickBooks);
  const isMorningConnected = connectedIntegrations.includes(INTEGRATION_PROVIDERS.morning);
  const isConnactConnected = connectedIntegrations.includes(INTEGRATION_PROVIDERS.connact);
  const isXeroConnected = connectedIntegrations.includes(INTEGRATION_PROVIDERS.xero);
  const isNetSuiteConnected = connectedIntegrations.includes(INTEGRATION_PROVIDERS.netSuite);
  const isStripeConnected = connectedIntegrations.includes(INTEGRATION_PROVIDERS.stripe);
  const isPaddleConnected = connectedIntegrations.includes(INTEGRATION_PROVIDERS.paddle);

  const handleMenuItemClick = (action?: () => void) => {
    return () => {
      handleMenuClose();
      action?.();
    };
  };

  return (
    <>
      <MenuItem
        onClick={handleSyncMenuOpen}
        disabled={disabled}
        {...getSelectorAttribute(dynamicInvoicePageSelectors.syncInvoiceMenu.syncInvoiceMenu)}
      >
        <ListItemIcon>
          <VyIcon
            icon="ArrowRotate"
            color="black"
            style={{
              rotate: '45deg',
              transform: 'scale(0.7)',
            }}
          />
        </ListItemIcon>
        <ListItemText>Sync</ListItemText>
        <ArrowSmallRight />
      </MenuItem>

      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        open={isSyncMenuOpen}
        anchorEl={syncMenuAnchorElement}
        onClose={handleSyncMenuClose}
      >
        <ListSubheader
          sx={{
            fontSize: '12px',
            color: '#7e8796',
            padding: '9px 16px 6px',
            lineHeight: '16px',
            background: 'transparent',
          }}
        >
          Invoices sync
        </ListSubheader>

        {isStripeConnected && (
          <MenuItem
            {...getSelectorAttribute(dynamicInvoicePageSelectors.syncInvoiceMenu.syncToStripe)}
            onClick={handleMenuItemClick(onSyncToStripe)}
            disabled={syncToStripeDisabled}
          >
            <Typography>Sync to Stripe</Typography>
          </MenuItem>
        )}

        {isNetSuiteConnected && (
          <MenuItem
            {...getSelectorAttribute(dynamicInvoicePageSelectors.syncInvoiceMenu.syncToNetSuite)}
            onClick={handleMenuItemClick(onSyncToNetSuite)}
            disabled={syncToNetSuiteDisabled}
          >
            <Typography>Sync to NetSuite</Typography>
          </MenuItem>
        )}

        {isQuickBooksConnected && (
          <MenuItem onClick={handleMenuItemClick(onSyncToQuickBooks)} disabled={!isInvoiceSyncable}>
            Sync to QuickBooks
          </MenuItem>
        )}

        {isMorningConnected && (
          <MenuItem onClick={handleMenuItemClick(onSyncToMorning)} disabled={!isInvoiceSyncable}>
            <Typography>Sync to Morning</Typography>
          </MenuItem>
        )}

        {isXeroConnected && (
          <MenuItem onClick={handleMenuItemClick(onSyncToXero)} disabled={!isInvoiceSyncable}>
            <Typography>Sync to Xero</Typography>
          </MenuItem>
        )}

        {isPaddleConnected && (
          <MenuItem onClick={handleMenuItemClick(onSyncToPaddle)} disabled={!isInvoiceSyncable}>
            <Typography>Sync to Paddle</Typography>
          </MenuItem>
        )}


        {isNetSuiteConnected && (
          <>
            <Divider />

            <ListSubheader
              sx={{
                fontSize: '12px',
                color: '#7e8796',
                padding: '9px 16px 6px',
                lineHeight: '16px',
                background: 'transparent',
              }}
            >
              Sales order creation
            </ListSubheader>
            <MenuItem
              {...getSelectorAttribute(dynamicInvoicePageSelectors.syncInvoiceMenu.syncInvoiceSalesOrder)}
              onClick={handleMenuItemClick(onSyncToNetSuiteSalesOrder)}
              disabled={syncToNetSuiteDisabled}
            >
              <Typography>Sync Invoice as Sales Order</Typography>
            </MenuItem>
          </>
        )}

        {isConnactConnected && (
          <>
            <Divider />

            <ListSubheader
              sx={{
                fontSize: '12px',
                color: '#7e8796',
                padding: '9px 16px 6px',
                lineHeight: '16px',
                background: 'transparent',
              }}
            >
              Marketplace usage sync
            </ListSubheader>
            <MenuItem onClick={handleMenuItemClick(onSyncToMarketplace)} disabled={!isInvoiceSyncable || isMarketplaceSynced}>
              <Typography>Upload Usage to Marketplace</Typography>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};
